(ns machtfit.employee-rules-table
  (:require [clojure.string :as str]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.employee-rules :refer [matches? aggregate-rules expand-rules filter-by-managed-employees filter-by-current-rule is-redundant? is-duplicate?]]
            [machtfit.multi-item-select :refer [multi-item-select]]
            [machtfit.utils :as utils]
            [re-frame.core :as rf]))

(def ^:private placeholder-all-value "Alle")
(def ^:private placeholder-none-value "Bitte auswählen...")

(defn- rule-condition [label class-suffix value]
  [:<>
   [:div {:class (str "rule-label-" class-suffix)}
    [:span label]]
   [:div {:class (str "rule-value-" class-suffix)}
    (if (seq? value)
      (into [:ul {:style {:font-weight "bold"}}] (map #(vector :li %) value))
      [:span {:style {:font-weight "bold"}} value])]])

(defn- rule-conditions [{:keys [rules-sub companies-sub places-of-business-sub managed-employees-sub employee-groups-sub] :as config} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])
        condition (:condition rule)
        only-company-selected? (= (keys condition) '(:company))
        companies @(rf/subscribe companies-sub)
        places-of-business @(rf/subscribe places-of-business-sub)
        employee-groups @(rf/subscribe employee-groups-sub)
        company @(rf/subscribe [::company-by-id companies-sub (:company condition)])
        selected-places-of-business-ids (-> rule :condition :places-of-business)
        selected-employee-group-ids (-> rule :condition :employee-groups)
        selected-place-of-business-names (sort (map #(->> % (utils/by-id places-of-business) :name) selected-places-of-business-ids))
        selected-employee-group-names (sort (map #(->> % (utils/by-id employee-groups) :name) selected-employee-group-ids))
        subsidiary (:subsidiary condition)
        occupation-group (:occupation-group condition)]
    [:<>
     (when (and (some? company) (or (> (count companies) 1) only-company-selected?))
       [rule-condition "Unternehmen:" "company" (:name company)])
     (when (some? subsidiary)
       [rule-condition "Unternehmensteil:" "subsidiary" subsidiary])
     (when (some? occupation-group)
       [rule-condition "Berufsgruppe:" "occupation-group" occupation-group])
     (when-not (empty? selected-place-of-business-names)
       [rule-condition "Standorte:" "places-of-business" selected-place-of-business-names])
     (when (seq selected-employee-group-names)
       [rule-condition "Mitarbeitergruppen:" "employee-groups" selected-employee-group-names])
     [:div.rule-errors
      [widgets/error-messages @(rf/subscribe [::rule-errors rules-sub managed-employees-sub config index])]]]))

(defn- rule-result [{:keys [rules-sub]} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])]
    (when (-> rule :result :subsidy-rate)
      [:div.rule-result (str "Fördersatz : " (-> rule :result :subsidy-rate) " %")])))

(defn- view-rule [config index]
  [:<>
   [rule-result config index]
   [rule-conditions config index]])

(rf/reg-event-fx ::set-company-condition
  (fn [_ [_ id on-set-rule-condition index value]]
    {:dispatch-n (list [on-set-rule-condition index :company value]
                       [on-set-rule-condition index :places-of-business nil]
                       [::set-selective-places-of-business id on-set-rule-condition index false])}))

(defn- edit-rule-company [{:keys [id rules-sub on-set-rule-condition] :as config} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])
        company-options @(rf/subscribe [::selectable-options-company config])]
    [:<>
     [:div.rule-label-company "Unternehmen: "]
     [:div.rule-value-company
      [widgets/select-box {:value (or (-> rule :condition :company) "")
                           :on-change #(rf/dispatch [::set-company-condition id on-set-rule-condition index (if (empty? %) nil (int %))])
                           :data company-options
                           :no-widget-init? true
                           :browser-default? true}]]]))

(defn- edit-rule-result [{:keys [rules-sub on-set-rule-result]} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])]
    (when (-> rule :result :subsidy-rate)
      [:<>
       [:div.rule-result
        [widgets/block-input2 {:value (-> rule :result :subsidy-rate)
                               :type widgets/type-percent-number
                               :label-text "Fördersatz"
                               :on-save #(rf/dispatch [on-set-rule-result index %])}]]])))

(rf/reg-event-fx ::set-selective-places-of-business
  (fn [{:keys [db]} [_ id on-set-rule-condition index selective?]]
    (cond-> {:db (assoc-in db [:ui id :selective-places-of-business?] selective?)}
      (not selective?) (assoc :dispatch [on-set-rule-condition index :places-of-business #{}]))))

(defn- selective-places-of-business-choice [{:keys
                                             [id
                                              places-of-business-selective-intro-text
                                              places-of-business-selective-outro-text
                                              places-of-business-not-selective-text
                                              places-of-business-selective-text
                                              on-set-rule-condition]} index]
  [:div.select-all {:class "valign-wrapper" :style {:margin-bottom "1em"}}
   (widgets/choice
    {:name (str id "-selective-places-of-business")
     :intro places-of-business-selective-intro-text
     :outro places-of-business-selective-outro-text
     :current-choice @(rf/subscribe [::selective-places-of-business? id])
     :choices [[false places-of-business-not-selective-text]
               [true places-of-business-selective-text]]
     :on-select #(rf/dispatch [::set-selective-places-of-business id on-set-rule-condition index %])})])

(rf/reg-event-fx ::set-selective-employee-groups
  (fn [{:keys [db]} [_ id on-set-rule-condition index selective?]]
    (cond-> {:db (assoc-in db [:ui id :selective-employee-groups?] selective?)}
      (not selective?) (assoc :dispatch [on-set-rule-condition index :employee-groups #{}]))))

(defn- selective-employee-group-choice [{:keys
                                         [id
                                          employee-groups-selective-intro-text
                                          employee-groups-selective-outro-text
                                          employee-groups-not-selective-text
                                          employee-groups-selective-text
                                          on-set-rule-condition]} index]
  [:div.select-all {:class "valign-wrapper"
                    :style {:margin-bottom "1em"}}
   (widgets/choice
    {:name (str id "-selective-employee-groups")
     :intro employee-groups-selective-intro-text
     :outro employee-groups-selective-outro-text
     :current-choice @(rf/subscribe [::selective-employee-groups? id])
     :choices [[false employee-groups-not-selective-text]
               [true employee-groups-selective-text]]
     :on-select #(rf/dispatch [::set-selective-employee-groups id on-set-rule-condition index %])})])

(rf/reg-sub ::selected-place-of-business-ids
  (fn [[_ id rules-sub]]
    [(rf/subscribe [::current-rule id rules-sub])])

  (fn [[current-rule] _]
    (or (get-in current-rule [:condition :places-of-business]) #{})))

(rf/reg-sub ::selected-employee-group-ids
  (fn [[_ id rules-sub]]
    [(rf/subscribe [::current-rule id rules-sub])])

  (fn [[current-rule] _]
    (or (get-in current-rule [:condition :employee-groups]) #{})))

(defn- edit-rule-places-of-business [{:keys [id rules-sub managed-employees-sub on-set-rule-condition] :as config} index]
  (let [selectable-places-of-business @(rf/subscribe [::selectable-options-place-of-business config])
        managed-employees @(rf/subscribe managed-employees-sub)
        admin-restricted-to-pobs? (and (some? managed-employees) (some #(some? (get-in % [:condition :place-of-business])) managed-employees))]
    (when (->> selectable-places-of-business (map :id) (some some?))
      [:<>
       [:div.rule-label-places-of-business "Standorte: "]
       [:div.rule-value-places-of-business
        (when-not admin-restricted-to-pobs?
          [selective-places-of-business-choice config index])
        (when (or admin-restricted-to-pobs? @(rf/subscribe [::selective-places-of-business? id]))
          [multi-item-select {:id id
                              :selected-stats-strings {:zero "kein Standort"
                                                       :one "ein Standort"
                                                       :multiple "Standorte"}
                              :match-stats-strings {:singular "Standort"
                                                    :plural "Standorte"}

                              :options-sub [::selectable-options-place-of-business config]
                              :selected-ids-sub [::selected-place-of-business-ids id rules-sub]
                              :on-selected-ids-changed #(rf/dispatch [on-set-rule-condition index :places-of-business %])}])]])))

(defn- edit-rule-employee-groups [{:keys [id rules-sub on-set-rule-condition] :as config} index]
  (let [selectable-employee-groups @(rf/subscribe [::selectable-options-employee-group config])]
    (when (->> selectable-employee-groups (map :id) (some some?))
      [:<>
       [:div.rule-label-employee-groups "Mitarbeitergruppen: "]
       [:div.rule-value-employee-groups
        [selective-employee-group-choice config index]
        (when @(rf/subscribe [::selective-employee-groups? id])
          [multi-item-select {:id id
                              :match-stats-strings {:singular "Gruppe"
                                                    :plural "Gruppen"}
                              :selected-stats-strings {:zero "keine Gruppe"
                                                       :one "eine Gruppe"
                                                       :multiple "Gruppen"}
                              :options-sub [::selectable-options-employee-group config]
                              :selected-ids-sub [::selected-employee-group-ids id rules-sub]
                              :on-selected-ids-changed #(rf/dispatch [on-set-rule-condition index :employee-groups %])}])]])))

(defn- edit-rule-occupation-group [{:keys [rules-sub on-set-rule-condition] :as config} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])
        occupation-group-options @(rf/subscribe [::selectable-options-occupation-group config])]
    (when-not (->> occupation-group-options (map :value) (= [nil]))
      [:<>
       [:div.rule-label-occupation-group "Berufsgruppe: "]
       [:div.rule-value-occupation-group
        (if (-> occupation-group-options count (> 1))
          [widgets/select-box {:value (or (-> rule :condition :occupation-group) "")
                               :on-change #(rf/dispatch [on-set-rule-condition index :occupation-group (if (or (empty? %) (= placeholder-all-value %)) nil %)])
                               :data occupation-group-options
                               :no-widget-init? true
                               :browser-default? true}]
          [:span {:style {:margin-left "9px"}} (-> occupation-group-options first :label)])]])))

(defn- edit-rule-subsidiary [{:keys [rules-sub on-set-rule-condition] :as config} index]
  (let [rule @(rf/subscribe [::rule-by-index rules-sub index])
        subsidiaries @(rf/subscribe [::selectable-options-subsidiary config])]
    (when-not (->> subsidiaries (map :value) (= [nil]))
      [:<>
       [:div.rule-label-subsidiary "Unternehmensteil: "]
       [:div.rule-value-subsidiary
        (if (-> subsidiaries count (> 1))
          [widgets/select-box {:value (or (-> rule :condition :subsidiary) "")
                               :on-change #(rf/dispatch [on-set-rule-condition index :subsidiary (if (or (empty? %) (= placeholder-all-value %)) nil %)])
                               :data subsidiaries
                               :no-widget-init? true
                               :browser-default? true}]
          [:span {:style {:margin-left "9px"}} (-> subsidiaries first :label)])]])))

(defn- edit-rule-conditions [{:keys [id rules-sub companies-sub managed-employees-sub] :as config} index]
  (let [companies @(rf/subscribe companies-sub)
        current-rule @(rf/subscribe [::current-rule id rules-sub])
        current-company (-> current-rule :condition :company)
        errors @(rf/subscribe [::rule-errors rules-sub managed-employees-sub config index])]
    [:<>
     (when (> (count companies) 1)
       [edit-rule-company config index])
     (when (some? current-company)
       [:<>
        [edit-rule-result config index]
        [edit-rule-occupation-group config index]
        [edit-rule-places-of-business config index]
        [edit-rule-employee-groups config index]
        [edit-rule-subsidiary config index]])
     (when-not (empty? errors)
       [:div.rule-errors
        [widgets/error-messages errors]])]))

(defn- rule-row [{:keys [id allow-no-rules? rules-sub on-delete-rule] :as config} index]
  (let [num-rules @(rf/subscribe [::num-rules rules-sub])
        editing? (= index @(rf/subscribe [::current-rule-index id]))
        rule @(rf/subscribe [::rule-by-index rules-sub index])]
    [:div.rule-row {:class (when editing? "editing")}
     [(if editing? edit-rule-conditions view-rule) config index]
     [:div.action1
      (when-not editing?
        [widgets/edit-button {:on-click #(rf/dispatch [::edit-employee-rule id index @(rf/subscribe [::rule-by-index rules-sub index])])}])
      (when (and editing? (some? (-> rule :condition :company)))
        [widgets/finish-button {:on-click #(rf/dispatch [::edit-employee-rule id nil nil])}])]
     [:div.action2
      (when (or (> num-rules 1) allow-no-rules?)
        [widgets/delete-button {:on-click #(rf/dispatch [on-delete-rule index])}])]]))

(rf/reg-sub ::selective-places-of-business?
  (fn [db [_ id]]
    (get-in db [:ui id :selective-places-of-business?] false)))

(rf/reg-sub ::selective-employee-groups?
  (fn [db [_ id]]
    (get-in db [:ui id :selective-employee-groups?] false)))

(rf/reg-sub ::num-rules
  (fn [[_ rules-sub]]
    [(rf/subscribe rules-sub)])

  (fn [[rules] _]
    (count rules)))

(rf/reg-sub ::current-rule-index
  (fn [db [_ id]]
    (get-in db [:ui id :current-rule-index])))

(rf/reg-sub ::rule-by-index
  (fn [[_ rules-sub _index]]
    [(rf/subscribe rules-sub)])

  (fn [[rules] [_ _rules-sub index]]
    (get rules index)))

(rf/reg-sub ::current-rule
  (fn [[_ id rules-sub]]
    [(rf/subscribe rules-sub)
     (rf/subscribe [::current-rule-index id])])

  (fn [[rules current-rule-index] _]
    (get rules current-rule-index)))

(rf/reg-sub ::company-by-id
  (fn [[_ companies-sub _company-id]]
    [(rf/subscribe companies-sub)])

  (fn [[companies] [_ _companies-sub company-id]]
    (utils/by-id companies company-id)))

(rf/reg-sub ::current-rule-company
  (fn [[_ id rules-sub companies-sub]]
    [(rf/subscribe companies-sub)
     (rf/subscribe [::current-rule id rules-sub])])

  (fn [[companies current-rule] _]
    (when-let [company-id (-> current-rule :condition :company)]
      (utils/by-id companies company-id))))

(rf/reg-sub ::filter-condition-options-by-existing-employees?
  (fn [[_ id rules-sub companies-sub]]
    [(rf/subscribe [::current-rule-company id rules-sub companies-sub])])

  (fn [[current-company] _]
    (-> current-company :filter-condition-options-by-existing-employees?)))

(rf/reg-sub ::rule-errors
  (fn [[_ rules-sub managed-employees-sub config index]]
    [(rf/subscribe [::rule-by-index rules-sub index])
     (rf/subscribe rules-sub)
     (rf/subscribe [::current-rule-index (:id config)])
     (rf/subscribe managed-employees-sub)])

  (fn [[rule rules current-rule-index managed-employees] [_ _rules-sub _managed-employees-sub config index]]
    (let [non-empty-rules (filterv #(some? (-> % :condition :company)) rules)]
      (cond
        (and (:rules-must-match-admin-rules? config)
             (some? managed-employees)
             (let [expanded-rules (-> rule
                                      list
                                      (expand-rules :places-of-business :place-of-business)
                                      (expand-rules :employee-groups :employee-group))]
               (not-every? (fn [expanded-rule] (some #(matches? (:condition %) (:condition expanded-rule)) managed-employees)) expanded-rules)))
        ["Sie dürfen Mitarbeiter mit diesem Regelsatz nicht verwalten. Bitte geben Sie eine gültige Kombination an."]

        (and
         (not= index current-rule-index) ;; Only show when not currently editing this rule
         (is-redundant? rule non-empty-rules))
        ["Diese Regel hat aufgrund einer anderen allgemeineren Regel keine Auswirkungen und wird daher nicht gespeichert."]

        (and
         (not= index current-rule-index) ;; Only show when not currently editing this rule
         (is-duplicate? rule index non-empty-rules))
        ["Diese Regel ist ein Duplikat einer vorherigen Regel und wird daher nicht gespeichert."]

        :else
        []))))

(defn- selectable-options [items rule-key managed-employees current-rule filter-by-existing-employees?]
  (let [placeholder (if (= rule-key :company) placeholder-none-value placeholder-all-value)]
    (as-> items $
          (concat [{:id nil :name placeholder}] $)
          (filter-by-managed-employees $ rule-key managed-employees)
          (filter-by-current-rule $ rule-key current-rule filter-by-existing-employees?)
          (for [item $] {:value (:id item) :label (:name item)})
          (sort-by #(str/lower-case (:label %)) $)
          (sort-by #(if (nil? (:value %)) 0 1) $)))) ;; Ensure placeholders are first item in options

(rf/reg-sub ::selectable-options-company
  (fn [[_ {:keys [companies-sub managed-employees-sub]}]]
    [(rf/subscribe companies-sub)
     (rf/subscribe managed-employees-sub)])

  (fn [[companies managed-employees] _]
    (selectable-options companies :company managed-employees nil false)))

(rf/reg-sub ::selectable-options-place-of-business
  (fn [[_ {:keys [id rules-sub places-of-business-sub managed-employees-sub companies-sub]}]]
    [(rf/subscribe places-of-business-sub)
     (rf/subscribe managed-employees-sub)
     (rf/subscribe [::current-rule id rules-sub])
     (rf/subscribe [::filter-condition-options-by-existing-employees? id rules-sub companies-sub])])

  (fn [[places-of-business managed-employees current-rule filter-by-existing-employees?] _]
    (-> places-of-business
        (filter-by-managed-employees :place-of-business managed-employees)
        (filter-by-current-rule :places-of-business current-rule filter-by-existing-employees?))))

(rf/reg-sub ::selectable-options-employee-group
  (fn [[_ {:keys [id rules-sub employee-groups-sub companies-sub]}]]
    [(rf/subscribe employee-groups-sub)
     (rf/subscribe [::current-rule id rules-sub])
     (rf/subscribe [::filter-condition-options-by-existing-employees? id rules-sub companies-sub])])

  (fn [[employee-groups
        current-rule
        filter-by-existing-employees?] _]

    (filter-by-current-rule
     employee-groups
     :employee-groups current-rule filter-by-existing-employees?)))

(rf/reg-sub ::selectable-options-occupation-group
  (fn [[_ {:keys [id rules-sub occupation-groups-sub managed-employees-sub companies-sub]}]]
    [(rf/subscribe occupation-groups-sub)
     (rf/subscribe managed-employees-sub)
     (rf/subscribe [::current-rule id rules-sub])
     (rf/subscribe [::filter-condition-options-by-existing-employees? id rules-sub companies-sub])])

  (fn [[occupation-groups managed-employees current-rule filter-by-existing-employees?] _]
    (selectable-options occupation-groups :occupation-group managed-employees current-rule filter-by-existing-employees?)))

(rf/reg-sub ::selectable-options-subsidiary
  (fn [[_ {:keys [id rules-sub subsidiaries-sub managed-employees-sub companies-sub]}]]
    [(rf/subscribe subsidiaries-sub)
     (rf/subscribe managed-employees-sub)
     (rf/subscribe [::current-rule id rules-sub])
     (rf/subscribe [::filter-condition-options-by-existing-employees? id rules-sub companies-sub])])

  (fn [[subsidiaries managed-employees current-rule filter-by-existing-employees?] _]
    (selectable-options subsidiaries :subsidiary managed-employees current-rule filter-by-existing-employees?)))

(rf/reg-event-fx ::add-employee-rule
  (fn [{:keys [db]} [_ id on-add-rule new-rule-template new-rule-index managed-employees companies]]
    (let [new-rule (cond-> {:condition {}}
                     (some? new-rule-template) (merge new-rule-template)
                     (= (count companies) 1) (assoc-in [:condition :company] (-> companies first :id))
                     (seq managed-employees) (merge (first (aggregate-rules managed-employees :place-of-business :places-of-business))))]
      {:db (assoc-in db [:ui id :current-rule-index] new-rule-index)
       :dispatch [on-add-rule new-rule]})))

(rf/reg-event-db ::edit-employee-rule
  (fn [db [_ id index rule]]
    (-> db
        (assoc-in [:ui id :current-rule-index] index)
        (assoc-in [:ui id :selective-places-of-business?] (and (some? rule) (not (empty? (get-in rule [:condition :places-of-business])))))
        (assoc-in [:ui id :selective-employee-groups?] (and (some? rule) (not (empty? (get-in rule [:condition :employee-groups]))))))))

(defn employee-rules-table [{:keys [id rules-sub companies-sub managed-employees-sub on-add-rule placeholder-text new-rule-template] :as config}]
  (let [num-rules @(rf/subscribe [::num-rules rules-sub])
        companies @(rf/subscribe companies-sub)
        managed-employees @(rf/subscribe managed-employees-sub)]
    [:div.employee-rules-table
     (if (= num-rules 0)
       [:div placeholder-text]
       (for [index (range num-rules)]
         ^{:key index}
         [rule-row config index]))
     [:div.button-bar
      [widgets/add-button {:title "Regel hinzufügen"
                           :on-click #(rf/dispatch [::add-employee-rule id on-add-rule new-rule-template num-rules managed-employees companies])}]]]))
